/**
* Licensed Materials - Property of IBM
* IBM Cognos Products: BI Dashboard
* (C) Copyright IBM Corp. 2022
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/
define([
], (
) => {

	const _isTextNode = (node) => node.nodeType === window.Node.TEXT_NODE;

	const _isTextFittedNode = (node) => {
		const classList = Array.from(node.classList);
		return classList.includes('textFitted');
	};

	const _selectRange = ({ startContainer, endContainer }) => {
		const selection = window.getSelection();
		const range = document.createRange();
		range.setStart(startContainer, 0);
		if (endContainer) {
			range.setEnd(endContainer, endContainer.length);
		} else {
			range.setEnd(startContainer, startContainer.length);
		}
		selection.removeAllRanges();
		selection.addRange(range);
	};

	const _selectNodeContents = (node) => {
		const selection = window.getSelection();
		const range = document.createRange();
		range.selectNodeContents(node);
		selection.removeAllRanges();
		selection.addRange(range);
	};

	const TEXT_FORMAT_NODE_NAMES = [
		'b', // Summernote use this tag to bold text by calling document.execCommand('bold')
		'B',
		'i', // Summernote use this tag to italic text  by calling document.execCommand('italic')
		'I',
		'u', // Summernote use this tag to underline text  by calling document.execCommand('underline'),
		'U'
	];

	class Range {
		select () {}

		/**
		 * Recursive walk descendents to find a text node that matches the textToMatch string
		 *
		 * @param {object} node - the node to walk its descendants to search for the text node that contain the textToMatch
		 * @param {string} textToMatch - the text string to search
		 *
		 * @return {object} the next node if one found
		 */
		findTextNode (node, textToMatch) {
			let foundNode;
			if (node) {
				if (_isTextNode(node) && (node.nodeValue === textToMatch)) {
					foundNode = node;
				} else {
					const childNodes = Array.from(node.childNodes);
					for (let count = 0; count < childNodes.length; count++) {
						const childNode = childNodes[count];
						foundNode = this.findTextNode(childNode, textToMatch);
						if (foundNode) {
							break;
						}
					}
				}
			}
			return foundNode;
		}
	}

	/**
	 * This class handle user selection occur on non-text node
	 */
	class CommonNodeContainerRange extends Range {
		constructor ({ range }) {
			super();
			this.node = range.commonAncestorContainer;
		}

		select () {
			_selectNodeContents(this.node);
		}

	}

	/*
	 * This class handle user selected text range occur in two text nodes: start container text node and end container text node
	 */
	class StartEndPointsTextRange extends Range {
		constructor ({ range }) {
			super();
			if (_isTextNode(range.startContainer) && _isTextNode(range.endContainer)) {
				this.startContainer = new CommonTextContainerRange ({
					selectedText: range.startContainer.nodeValue.slice(range.startOffset),
					range: {
						commonAncestorContainer: range.startContainer
					}
				});
				this.endContainer = new CommonTextContainerRange ({
					selectedText: range.endContainer.nodeValue.slice(0, range.endOffset),
					range: {
						commonAncestorContainer: range.endContainer
					}
				});
			}
		}

		select () {
			if (this._isValidRange()) {
				const startContainer = this.findTextNode(this.startContainer.getNode(), this.startContainer.getSelectedText());
				const endContainer = this.findTextNode(this.endContainer.getNode(), this.endContainer.getSelectedText());
				if (startContainer && endContainer) {
					_selectRange({ startContainer, endContainer });
				}
			}
		}

		_isValidRange () {
			return this.startContainer && this.endContainer;
		}
	}

	/**
	 * This class handle select text range within the same text node
 	 */
	class CommonTextContainerRange extends Range  {
		constructor ({ selectedText, range }) {
			super();
			this.selectedText = selectedText;
			this.commonAncestorContainer = range.commonAncestorContainer;
			let node = range.commonAncestorContainer.parentNode;
			// Skip over these nodes TEXT_FORMAT_NODE_NAMES since bold, italic, underline can be turn on/off. When off from on these nodes get remove from
			// the DOM so they are not reliable as a starting point to search for selected text so skip over them until an ancestor is found that is not one of these tags
			while (node) {
				if (_isTextFittedNode(node)) {
					// Reach the top most node in TextEditor, set to null
					node = null;
				} else if (!TEXT_FORMAT_NODE_NAMES.includes(node.nodeName)) {
					break;
				}
				node = node.parentNode;
			}
			this.node = node;
		}

		getNode() {
			return this.node;
		}

		getSelectedText () {
			return this.selectedText;
		}

		setSelectedText (value) {
			this.selectedText = value;
		}

		select () {
			const startContainer = this.findTextNode(this.node, this.selectedText);
			if (startContainer) {
				_selectRange({ startContainer });
			}
		}

		_getNodeClassListAsString (node) {
			return JSON.stringify(node && node.classList ? Array.from(node.classList).sort() : []);
		}
	}

	/**
	 * Any time there is a change in Summernote contents when new nodes are inserted or deleted when styles get apply
	 * the browser updates user selection range base on the context of the nodes added or deleted.
	 * When this happen the original user selected text range got lost.
	 * The purpose of this class is to store user text selection range content and reselect the range after styles are applied
	 *
	 */
	return class SelectedTextRange {
		/**
		 * Constucts an instance of this class
		 *
		 * @param {object}
		 * @param {object.string} selectedText - only valid when the selected text range is within a text node
		 * @param {object.range} range - the current user selected text range
		 */
		constructor ({ selectedText, range }) {
			const hasSelectedText = range && !range.collapsed; //true when there is a cursor caret instead of selected text
			if (hasSelectedText) {
				const isTextNode = _isTextNode(range.commonAncestorContainer);
				if (isTextNode) {
					this.selectRange = new CommonTextContainerRange({ selectedText, range });
				} else if (range.startContainer && range.startContainer === range.endContainer) {
					this.selectRange = new CommonNodeContainerRange({ range });
				} else {
					this.selectRange = new StartEndPointsTextRange({ range });
				}
			}
			return {
				select: this.select.bind(this)
			};
		}

		/**
		 * Call to select the text range
		 */
		select () {
			this.selectRange && this.selectRange.select();
		}
	};
});
